import { isNil } from 'lodash';

import NavigationControls from './NavigationControls';
import PageSizeSelector from './PageSizeSelector';
import TotalResultsHeader from './TotalResultsHeader';
import { TablePaginationProps, TableRowData } from '../types';

import cn from '~/utils/cn';

const TablePagination = <T extends TableRowData>({ table, pagination, className }: TablePaginationProps<T>) => {
  if (!pagination?.enabled) {
    return null;
  }

  const currentPage = table.getState().pagination.pageIndex + 1;
  const totalPages = table.getPageCount();
  const pageSize = table.getState().pagination.pageSize;
  const totalRows =
    pagination.manual && !isNil(pagination.totalRows) ? pagination.totalRows : table.getFilteredRowModel().rows.length;

  const startRow = (currentPage - 1) * pageSize + 1;
  const endRow = Math.min(currentPage * pageSize, totalRows);

  const handlePreviousPage = () => {
    table.previousPage();
  };

  const handleNextPage = () => {
    table.nextPage();
  };

  const handlePageSizeChange = (newPageSize: number) => {
    table.setPageSize(newPageSize);
  };

  const handlePageChange = (pageNumber: number) => {
    table.setPageIndex(pageNumber - 1);
  };

  return (
    <div className={cn('flex items-center justify-between bg-white px-4 py-4', className)}>
      {/* Left side - Row info and page size selector */}
      <div className="flex items-center gap-40">
        {!pagination.manual && <TotalResultsHeader startRow={startRow} endRow={endRow} totalRows={totalRows} />}

        {!!pagination.showPageSizeSelector && pagination.pageSizeOptions && (
          <PageSizeSelector
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={pagination.pageSizeOptions}
          />
        )}
      </div>

      {/* Right side - Navigation controls */}
      <NavigationControls
        currentPage={currentPage}
        totalPages={totalPages}
        canPreviousPage={table.getCanPreviousPage()}
        canNextPage={table.getCanNextPage()}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default TablePagination;
